import React, { useState } from "react";
import { Modal, Form, Button, SelectPicker, Input, Checkbox } from "rsuite";
import FileDownloadIcon from "@rsuite/icons/FileDownload";
import CheckIcon from "@rsuite/icons/Check";
import "./index.css";

const KRISHNAKALI_USER_ID = "U06HATH4N3S";
const PURVI_USER_ID = "U065Z3ZN98R";
const slackWebhookUrl =
  "https://hooks.slack.com/services/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";

const Index = ({ modalOpen, setModalOpen }) => {
  const [name, setName] = useState("");
  const [phone, setPhone] = useState("");
  const [enquiryPurpose, setEnquiryPurpose] = useState("");
  const [remarks, setRemarks] = useState("");
  const [optInValue, setOptInValue] = useState(true);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [resumeLink, setResumeLink] = useState("");

  const handleClose = () => {
    setModalOpen(false);
  };
  const optionsData = [
    "MarketPlace",
    "MetalCloud",
    "Careers",
    "Request Demo",
    "Others",
  ].map((items) => ({ label: items, value: items }));

  /**
   * Validates the form fields for the enquiry form and returns validation status.
   *
   * The function checks the following criteria:
   * - Name must only contain alphabets and spaces.
   * - Phone must only contain numeric characters.
   * - Enquiry purpose must be selected.
   * - Remarks cannot be empty.
   *
   * If any field is invalid, an alert is displayed with the first validation error encountered.
   *
   * @returns {boolean} - Returns `true` if all fields are valid, otherwise `undefined`.
   *
   */
  const validateFields = () => {
    const newErrors = {};
    // Validate form fields
    const namePattern = /^[a-zA-Z\s]*$/;
    const phonePattern = /^\d+$/;

    if (!name || !namePattern.test(name)) {
      newErrors.name = "Please enter a valid name (alphabets and spaces only).";
    }

    if (!phone || !phonePattern.test(phone)) {
      newErrors.phone = "Please enter a valid phone number (numbers only).";
    }

    if (!enquiryPurpose) {
      newErrors.enquiryPurpose = "Please select an enquiry purpose.";
    }

    if (!remarks) {
      newErrors.remarks = "Remarks cannot be empty.";
    }

    if (Object.keys(newErrors).length !== 0) {
      alert(Object.values(newErrors)[0]);
    } else return true; // Return true if no errors
  };

  const handleSubmit = async (e) => {
    if (isSubmitting) return;

    if (!validateFields()) return;

    setIsSubmitting(true); // Disable the button

    const payload = {
      blocks: [
        {
          type: "section",
          text: {
            type: "mrkdwn",
            text: `<@${
              enquiryPurpose === "Careers" ? KRISHNAKALI_USER_ID : PURVI_USER_ID
            }> You have a new enquiry: ✨`,
          },
        },
        {
          type: "section",
          fields: [
            {
              type: "mrkdwn",
              text: `*Name:*\n${name}`,
            },
            {
              type: "mrkdwn",
              text: `*Contact Number:*\n${phone}`,
            },
            {
              type: "mrkdwn",
              text: `*Enquiry Purpose:*\n${
                enquiryPurpose || "No purpose specified"
              }`,
            },
            {
              type: "mrkdwn",
              text: `*Remarks:*\n${remarks || "No remarks provided"}`,
            },
            {
              type: "mrkdwn",
              text: `*WhatsApp Notifications:*\n${optInValue ? "Yes" : "No"}`,
            },
            ...(enquiryPurpose === "Careers"
              ? [
                  {
                    type: "mrkdwn",
                    text: `*Resume Link:*\n${resumeLink || "No link provided"}`,
                  },
                ]
              : []),
          ],
        },
      ],
    };

    try {
      await notifyOnSlack("NP_WEBSITE", JSON.stringify(payload));
      await fetch(process.env.REACT_APP_GSCRIPT_URL, {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          name,
          contactNumber: phone,
          enquiryPurpose,
          remarks,
          optInWhatsApp: optInValue,
          resumeLink,
        }),
      });

      setModalOpen(false);
    } catch (error) {
      alert("Error submitting enquiry");
      setIsSubmitting(false);
    } finally {
      setName("");
      setPhone("");
      setEnquiryPurpose("");
      setRemarks("");
      setResumeLink("");
      setTimeout(() => {
        setIsSubmitting(false);
      }, 60000);
    }
  };

  const notifyOnSlack = async (channel, message) => {
    try {
      const response = await fetch(slackWebhookUrl, {
        method: "POST",
        mode: "no-cors",
        headers: {
          "Content-Type": "application/json",
        },
        body: message,
      });

      return response;
    } catch (error) {
      console.error("Fetch error:", error);
      throw error;
    }
  };

  const handleDownload = async () => {
    try {
      const response = await fetch(
        "https://img1.nowpurchase.com/foundry/pitch_deck_sales.pdf"
      );
      if (!response.ok) {
        throw new Error("Failed to fetch the file");
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = "Brochure.pdf";
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error("Download error:", error);
      alert("Error downloading file");
    }
  };
  return (
    <Modal open={modalOpen} onClose={handleClose}>
      <Modal.Header>
        <Modal.Title>Brochure Download / Enquiry Form</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form fluid onSubmit={handleSubmit}>
          <Form.Group>
            <Form.ControlLabel for="name">
              <span>Name</span>
            </Form.ControlLabel>
            <Form.Control
              name="name"
              type="text"
              placeholder="Enter your name here"
              Required
              value={name}
              onChange={(value) => setName(value)}
            />
            {/* <Form.HelpText>Required</Form.HelpText> */}
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel for="phone">
              <span>Contact Number</span>
            </Form.ControlLabel>
            <Form.Control
              name="phone"
              type="tel"
              placeholder="Enter your contact number"
              value={phone}
              onChange={(value) => setPhone(value)}
              required
            />
          </Form.Group>

          <Form.Group>
            <Form.ControlLabel for="entry_purpose">
              <span>Enquiry Purpose</span>
            </Form.ControlLabel>
            {/* <Form.Control
            name="entry_purpose"
            componentClass="select"
            placeholder="Select"
            required
          > */}

            <SelectPicker
              data={optionsData}
              value={enquiryPurpose}
              placeholder="Select Enquiry Purpose"
              searchable={false}
              size="sm"
              className="modalSelectPicker"
              onChange={(value) => setEnquiryPurpose(value)}
              style={{
                display: "flex",
                fontSize: "12px",
              }}
            />
            {/* </Form.Control> */}
          </Form.Group>

          {enquiryPurpose === "Careers" && (
            <Form.Group>
              <Form.ControlLabel for="resume_link">
                <span>Resume Link</span>
              </Form.ControlLabel>
              <Input
                value={resumeLink}
                required
                onChange={(value) => setResumeLink(value)}
                placeholder="Enter your resume link here"
              />
            </Form.Group>
          )}
          <Form.Group>
            <Form.ControlLabel for="remark">
              <span>Remark</span>
            </Form.ControlLabel>
            <Input
              value={remarks}
              onChange={(value) => setRemarks(value)}
              as="textarea"
              name="remark"
              rows={7}
              placeholder="Enter your remarks here"
            />
          </Form.Group>
          <Form.Group>
            <Checkbox
              onChange={() => setOptInValue((val) => !val)}
              value={optInValue}
              defaultChecked
            >
              Opt in for WhatsApp Notfications
            </Checkbox>
          </Form.Group>

          <Form.Group style={{ float: "right" }}>
            <Button
              appearance="subtle"
              onClick={handleDownload}
              style={{ marginRight: "20px" }}
            >
              <FileDownloadIcon /> Download Brochure
            </Button>
            <Button disabled={isSubmitting} type="submit">
              <CheckIcon /> Submit Enquiry
            </Button>
          </Form.Group>
        </Form>
      </Modal.Body>
    </Modal>
  );
};

export default Index;
